import { useCallback, useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { Permission, RolePermissions, UserRole } from '@/types/global.types';

export const usePermissions = () => {
  // Use individual selectors for better reactivity
  const user = useAuthStore((state) => state.user);
  const viewAsRole = useAuthStore((state) => state.viewAsRole);
  const getEffectiveRole = useAuthStore((state) => state.getEffectiveRole);
  const storePermissions = useAuthStore((state) => state.permissions);
  const permissionsLoaded = useAuthStore((state) => state.permissionsLoaded);

  // Get the effective role (considering "View As" mode)
  const effectiveRole = useMemo(() => {
    return getEffectiveRole();
  }, [getEffectiveRole, user, viewAsRole]);

  // Get all permissions - use store permissions if loaded from API, otherwise fallback to role defaults
  const permissions = useMemo((): Permission[] => {
    // If permissions have been loaded from API and we have permissions, use them
    if (permissionsLoaded && storePermissions.length > 0) {
      return storePermissions;
    }
    // If permissions loaded but empty (user has no permissions), return empty
    if (permissionsLoaded && storePermissions.length === 0) {
      return [];
    }
    // Fallback to role defaults while waiting for API
    if (!effectiveRole) return [];
    return RolePermissions[effectiveRole] || [];
  }, [permissionsLoaded, storePermissions, effectiveRole]);

  // Check if user has a specific permission - depends on permissions array for reactivity
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  // Check if user has any of the specified permissions - depends on permissions array for reactivity
  const hasAnyPermission = useCallback(
    (perms: Permission[]): boolean => {
      return perms.some(p => permissions.includes(p));
    },
    [permissions]
  );

  // Check if user has all specified permissions - depends on permissions array for reactivity
  const hasAllPermissions = useCallback(
    (perms: Permission[]): boolean => {
      return perms.every(p => permissions.includes(p));
    },
    [permissions]
  );

  // Check if user has a specific role
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      return effectiveRole === role;
    },
    [effectiveRole]
  );

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => {
      return effectiveRole !== null && roles.includes(effectiveRole);
    },
    [effectiveRole]
  );

  // Check if user is at least a certain role level
  const isAtLeastRole = useCallback(
    (minimumRole: UserRole): boolean => {
      if (!effectiveRole) return false;

      const roleHierarchy: UserRole[] = [
        UserRole.SUPER_ADMIN,
        UserRole.DIRECTOR_GENERAL,
        UserRole.BRANCH_MANAGER,
        UserRole.PEDAGOGICAL_DIRECTOR,
        UserRole.FINANCIAL_MANAGER,
        UserRole.MARKETING_MANAGER,
        UserRole.COMMERCIAL_MANAGER,
        UserRole.PROGRAM_HEAD,
        UserRole.ACADEMIC_COORDINATOR,
        UserRole.TEACHER,
        UserRole.STUDENT,
        UserRole.PARENT,
      ];

      const currentIndex = roleHierarchy.indexOf(effectiveRole);
      const minimumIndex = roleHierarchy.indexOf(minimumRole);

      return currentIndex !== -1 && currentIndex <= minimumIndex;
    },
    [effectiveRole]
  );

  // Check if user can access institution-level features
  const canAccessInstitution = useCallback((): boolean => {
    if (!user) return false;

    // Super admin can access all institutions
    if (user.role === UserRole.SUPER_ADMIN) return true;

    // Other roles need a valid tenant
    return user.tenantId !== null;
  }, [user]);

  // Check if user can manage students
  const canManageStudents = useCallback((): boolean => {
    return hasPermission(Permission.STUDENT_VIEW);
  }, [hasPermission]);

  // Check if user can create students
  const canCreateStudents = useCallback((): boolean => {
    return hasPermission(Permission.STUDENT_CREATE);
  }, [hasPermission]);

  // Check if user can bulk create students (import en masse)
  const canBulkCreateStudents = useCallback((): boolean => {
    return hasPermission(Permission.STUDENT_BULK_CREATE);
  }, [hasPermission]);

  // Check if user can manage staff
  const canManageStaff = useCallback((): boolean => {
    return hasPermission(Permission.STAFF_VIEW);
  }, [hasPermission]);

  // Check if user can manage permissions
  const canManagePermissions = useCallback((): boolean => {
    return hasPermission(Permission.PERMISSION_MANAGE);
  }, [hasPermission]);

  // Check if user can view financial data
  const canViewFinancials = useCallback((): boolean => {
    return hasAnyPermission([
      Permission.PAYMENTS_VIEW,
      Permission.PAYMENTS_MANAGE,
    ]);
  }, [hasAnyPermission]);

  // Check if user can manage academic content
  const canManageAcademics = useCallback((): boolean => {
    return hasAnyPermission([
      Permission.GRADES_VIEW,
      Permission.GRADES_EDIT,
      Permission.GRADES_VALIDATE,
    ]);
  }, [hasAnyPermission]);

  return {
    effectiveRole,
    permissions,
    permissionsLoaded,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAtLeastRole,
    canAccessInstitution,
    canManageStudents,
    canCreateStudents,
    canBulkCreateStudents,
    canManageStaff,
    canManagePermissions,
    canViewFinancials,
    canManageAcademics,
  };
};

// Utility type for permission-based rendering
export type PermissionCheck = Permission | Permission[] | ((permissions: Permission[]) => boolean);

// Helper function to check permissions
export const checkPermission = (
  userPermissions: Permission[],
  check: PermissionCheck
): boolean => {
  if (typeof check === 'function') {
    return check(userPermissions);
  }

  if (Array.isArray(check)) {
    return check.some((p) => userPermissions.includes(p));
  }

  return userPermissions.includes(check);
};
